import {
  type FigmaTemplateLayoutBox,
  type FigmaTemplateNode,
} from "./figma-template-ontology-benchmark";

export const FIGMA_TEMPLATE_REFERENCE_PARITY_SCHEMA_VERSION =
  "cogo.figma-template-reference-parity.v1";

export type FigmaTemplateReferenceParityStatus = "pass" | "fail";

export type FigmaTemplateReferenceParityMetricName =
  | "source-node-coverage"
  | "root-frame-geometry"
  | "layout-geometry"
  | "compiled-source-purity";

export interface FigmaTemplateReferenceParityMetric {
  name: FigmaTemplateReferenceParityMetricName;
  status: FigmaTemplateReferenceParityStatus;
  score: number;
  threshold: number;
  evidenceRefs: string[];
  reasons: string[];
}

export interface FigmaTemplateReferenceParityReport {
  schemaVersion: typeof FIGMA_TEMPLATE_REFERENCE_PARITY_SCHEMA_VERSION;
  status: FigmaTemplateReferenceParityStatus;
  score: number;
  threshold: number;
  sourceNodeCount: number;
  matchedNodeCount: number;
  missingSourceNodeRefs: string[];
  metrics: FigmaTemplateReferenceParityMetric[];
  evidenceRefs: string[];
  reasons: string[];
}

export interface FigmaTemplateCompiledNode {
  id?: string;
  type?: string;
  name?: string;
  text?: string;
  className?: string;
  style?: Record<string, unknown>;
  sourceDesign?: {
    figmaNodeId?: unknown;
    [key: string]: unknown;
  };
  dataAttributes?: {
    figmaNodeId?: unknown;
    [key: string]: unknown;
  };
  children?: readonly FigmaTemplateCompiledNode[];
  [key: string]: unknown;
}

export interface BuildFigmaTemplateReferenceParityOptions {
  layoutTolerancePercentPoints?: number;
  rootTolerancePx?: number;
  layoutGeometryThreshold?: number;
}

interface SourceNodeEntry {
  node: FigmaTemplateNode;
  parent?: FigmaTemplateNode;
}

interface CssNumber {
  value: number;
  unit: "%" | "px" | "number";
}

const DEFAULT_LAYOUT_TOLERANCE_PERCENT_POINTS = 0.02;
const DEFAULT_ROOT_TOLERANCE_PX = 0.5;
const DEFAULT_LAYOUT_GEOMETRY_THRESHOLD = 0.92;
const REPORT_THRESHOLD = 1;

export function buildFigmaTemplateReferenceParity(
  sourceRoot: FigmaTemplateNode,
  compiledRoot: FigmaTemplateCompiledNode,
  options: BuildFigmaTemplateReferenceParityOptions = {},
): FigmaTemplateReferenceParityReport {
  const layoutTolerancePercentPoints =
    options.layoutTolerancePercentPoints ??
    DEFAULT_LAYOUT_TOLERANCE_PERCENT_POINTS;
  const rootTolerancePx = options.rootTolerancePx ?? DEFAULT_ROOT_TOLERANCE_PX;
  const layoutGeometryThreshold =
    options.layoutGeometryThreshold ?? DEFAULT_LAYOUT_GEOMETRY_THRESHOLD;
  const sourceEntries = flattenSourceNodes(sourceRoot).filter(({ node }) =>
    hasUsableLayout(node.layout),
  );
  const compiledByFigmaNodeId = indexCompiledNodesByFigmaNodeId(compiledRoot);
  const missingSourceNodeRefs = sourceEntries
    .filter(({ node }) => !compiledByFigmaNodeId.has(node.id))
    .map(({ node }) => sourceNodeRef(node));

  const coverageMetric = referenceParityMetric({
    name: "source-node-coverage",
    score:
      sourceEntries.length > 0
        ? roundScore(
            (sourceEntries.length - missingSourceNodeRefs.length) /
              sourceEntries.length,
          )
        : 0,
    threshold: 1,
    evidenceRefs: sourceEntries
      .filter(({ node }) => compiledByFigmaNodeId.has(node.id))
      .map(({ node }) => sourceNodeRef(node)),
    reasons:
      sourceEntries.length === 0
        ? ["Figma source has no layout-bearing nodes to compare."]
        : missingSourceNodeRefs.length > 0
          ? [
              `Compiled Cogo tree is missing ${missingSourceNodeRefs.length}/${sourceEntries.length} source layout node(s).`,
              ...missingSourceNodeRefs.map(
                (ref) => `Missing compiled node for ${ref}.`,
              ),
            ]
          : [],
  });

  const rootMetric = buildRootFrameGeometryMetric({
    sourceRoot,
    compiledRoot,
    rootTolerancePx,
  });
  const layoutMetric = buildLayoutGeometryMetric({
    sourceEntries,
    compiledByFigmaNodeId,
    layoutTolerancePercentPoints,
    threshold: layoutGeometryThreshold,
  });
  const compiledSourcePurityMetric =
    buildCompiledSourcePurityMetric(compiledRoot);
  const metrics = [
    coverageMetric,
    rootMetric,
    layoutMetric,
    compiledSourcePurityMetric,
  ];
  const score = roundScore(
    metrics.reduce((sum, metric) => sum + metric.score, 0) / metrics.length,
  );
  const reasons = metrics.flatMap((metric) => metric.reasons);
  const status =
    metrics.every((metric) => metric.status === "pass") &&
    score >= REPORT_THRESHOLD
      ? "pass"
      : "fail";

  return {
    schemaVersion: FIGMA_TEMPLATE_REFERENCE_PARITY_SCHEMA_VERSION,
    status,
    score,
    threshold: REPORT_THRESHOLD,
    sourceNodeCount: sourceEntries.length,
    matchedNodeCount: sourceEntries.length - missingSourceNodeRefs.length,
    missingSourceNodeRefs,
    metrics,
    evidenceRefs: unique(metrics.flatMap((metric) => metric.evidenceRefs)),
    reasons,
  };
}

function buildRootFrameGeometryMetric(input: {
  sourceRoot: FigmaTemplateNode;
  compiledRoot: FigmaTemplateCompiledNode;
  rootTolerancePx: number;
}): FigmaTemplateReferenceParityMetric {
  const sourceLayout = input.sourceRoot.layout;
  const style = input.compiledRoot.style ?? {};
  const checks: Array<{
    passed: boolean;
    evidenceRef: string;
    reason: string;
  }> = [];

  checks.push({
    passed: hasUsableLayout(sourceLayout),
    evidenceRef: sourceNodeRef(input.sourceRoot),
    reason: "Figma source root is missing usable width/height geometry.",
  });

  checks.push({
    passed: style.position === "relative",
    evidenceRef: compiledNodeRef(input.compiledRoot),
    reason: "Compiled root frame must preserve relative positioning.",
  });
  checks.push({
    passed:
      parseCssNumber(style.width)?.unit === "%" &&
      parseCssNumber(style.width)?.value === 100,
    evidenceRef: compiledNodeRef(input.compiledRoot),
    reason: "Compiled root frame must use width: 100% for responsive scaling.",
  });
  checks.push({
    passed:
      typeof sourceLayout?.width === "number" &&
      nearlyEqualCssPx(
        style.maxWidth,
        sourceLayout.width,
        input.rootTolerancePx,
      ),
    evidenceRef: compiledNodeRef(input.compiledRoot),
    reason: `Compiled root maxWidth must match Figma source width ${sourceLayout?.width ?? "unknown"}px.`,
  });
  checks.push({
    passed:
      typeof sourceLayout?.width === "number" &&
      typeof sourceLayout?.height === "number" &&
      nearlyEqualAspectRatio(
        style.aspectRatio,
        sourceLayout.width,
        sourceLayout.height,
      ),
    evidenceRef: compiledNodeRef(input.compiledRoot),
    reason: `Compiled root aspectRatio must match Figma source frame ${sourceLayout?.width ?? "unknown"} / ${sourceLayout?.height ?? "unknown"}.`,
  });
  checks.push({
    passed: style.overflow === "hidden",
    evidenceRef: compiledNodeRef(input.compiledRoot),
    reason: "Compiled root frame must clip overflow to the Figma source frame.",
  });

  return metricFromChecks("root-frame-geometry", 1, checks);
}

function buildLayoutGeometryMetric(input: {
  sourceEntries: readonly SourceNodeEntry[];
  compiledByFigmaNodeId: ReadonlyMap<string, FigmaTemplateCompiledNode>;
  layoutTolerancePercentPoints: number;
  threshold: number;
}): FigmaTemplateReferenceParityMetric {
  const checks: Array<{
    passed: boolean;
    evidenceRef: string;
    reason: string;
  }> = [];
  const childEntries = input.sourceEntries.filter(
    ({ parent }) => parent && hasUsableLayout(parent.layout),
  );

  for (const { node, parent } of childEntries) {
    const compiledNode = input.compiledByFigmaNodeId.get(node.id);
    if (!compiledNode) {
      checks.push({
        passed: false,
        evidenceRef: sourceNodeRef(node),
        reason: `Compiled Cogo tree has no sourceDesign/dataAttributes figmaNodeId for ${sourceNodeRef(node)}.`,
      });
      continue;
    }
    checks.push({
      passed: compiledNode.style?.position === "absolute",
      evidenceRef: compiledNodeRef(compiledNode),
      reason: `${sourceNodeRef(node)} must compile to absolute positioning inside its Figma parent frame.`,
    });
    const parentLayout = parent?.layout;
    if (!node.layout || !parentLayout) continue;
    checks.push(
      layoutCheck({
        property: "left",
        actual: compiledNode.style?.left,
        expectedPercent:
          (((node.layout.x ?? 0) - (parentLayout.x ?? 0)) /
            parentLayout.width!) *
          100,
        expectedPx: (node.layout.x ?? 0) - (parentLayout.x ?? 0),
        tolerancePercentPoints: input.layoutTolerancePercentPoints,
        node,
        compiledNode,
      }),
      layoutCheck({
        property: "top",
        actual: compiledNode.style?.top,
        expectedPercent:
          (((node.layout.y ?? 0) - (parentLayout.y ?? 0)) /
            parentLayout.height!) *
          100,
        expectedPx: (node.layout.y ?? 0) - (parentLayout.y ?? 0),
        tolerancePercentPoints: input.layoutTolerancePercentPoints,
        node,
        compiledNode,
      }),
      layoutCheck({
        property: "width",
        actual: compiledNode.style?.width,
        expectedPercent: (node.layout.width! / parentLayout.width!) * 100,
        expectedPx: node.layout.width!,
        tolerancePercentPoints: input.layoutTolerancePercentPoints,
        node,
        compiledNode,
      }),
      layoutCheck({
        property: "height",
        actual: compiledNode.style?.height,
        expectedPercent: (node.layout.height! / parentLayout.height!) * 100,
        expectedPx: node.layout.height!,
        tolerancePercentPoints: input.layoutTolerancePercentPoints,
        node,
        compiledNode,
      }),
    );
  }

  if (childEntries.length === 0) {
    checks.push({
      passed: false,
      evidenceRef: sourceNodeRef(input.sourceEntries[0]?.node),
      reason:
        "Figma source has no child layout geometry to compare against the compiled tree.",
    });
  }

  return metricFromChecks("layout-geometry", input.threshold, checks);
}

function buildCompiledSourcePurityMetric(
  compiledRoot: FigmaTemplateCompiledNode,
): FigmaTemplateReferenceParityMetric {
  const compiledNodes = flattenCompiledNodes(compiledRoot);
  const sourceBackedNodes = compiledNodes.filter(readCompiledFigmaNodeId);
  const sourceLessNodes = compiledNodes.filter(
    (node) => !readCompiledFigmaNodeId(node),
  );
  return referenceParityMetric({
    name: "compiled-source-purity",
    score:
      compiledNodes.length > 0
        ? roundScore(sourceBackedNodes.length / compiledNodes.length)
        : 0,
    threshold: 1,
    evidenceRefs: sourceBackedNodes.map(compiledNodeRef),
    reasons:
      sourceLessNodes.length > 0
        ? sourceLessNodes.map(
            (node) =>
              `${compiledNodeRef(node)} is not backed by a Figma source node; the template compiler must not synthesize visual nodes during reference validation.`,
          )
        : compiledNodes.length === 0
          ? ["Compiled Cogo tree has no nodes to compare."]
          : [],
  });
}

function layoutCheck(input: {
  property: "left" | "top" | "width" | "height";
  actual: unknown;
  expectedPercent: number;
  expectedPx: number;
  tolerancePercentPoints: number;
  node: FigmaTemplateNode;
  compiledNode: FigmaTemplateCompiledNode;
}): { passed: boolean; evidenceRef: string; reason: string } {
  const parsed = parseCssNumber(input.actual);
  const passed =
    parsed?.unit === "%"
      ? Math.abs(parsed.value - input.expectedPercent) <=
        input.tolerancePercentPoints
      : parsed?.unit === "px" || parsed?.unit === "number"
        ? Math.abs(parsed.value - input.expectedPx) <= 0.5
        : false;
  return {
    passed,
    evidenceRef: compiledNodeRef(input.compiledNode),
    reason: `${sourceNodeRef(input.node)} ${input.property} geometry mismatch: expected ${roundScore(input.expectedPercent)}% or ${roundScore(input.expectedPx)}px, received ${String(input.actual ?? "missing")}.`,
  };
}

function metricFromChecks(
  name: FigmaTemplateReferenceParityMetricName,
  threshold: number,
  checks: readonly { passed: boolean; evidenceRef: string; reason: string }[],
): FigmaTemplateReferenceParityMetric {
  const score =
    checks.length > 0
      ? roundScore(
          checks.filter((check) => check.passed).length / checks.length,
        )
      : 0;
  return referenceParityMetric({
    name,
    score,
    threshold,
    evidenceRefs: unique(
      checks.filter((check) => check.passed).map((check) => check.evidenceRef),
    ),
    reasons: checks
      .filter((check) => !check.passed)
      .map((check) => check.reason),
  });
}

function referenceParityMetric(input: {
  name: FigmaTemplateReferenceParityMetricName;
  score: number;
  threshold: number;
  evidenceRefs: string[];
  reasons: string[];
}): FigmaTemplateReferenceParityMetric {
  return {
    ...input,
    status: input.score >= input.threshold ? "pass" : "fail",
    reasons:
      input.score >= input.threshold
        ? []
        : input.reasons.length > 0
          ? input.reasons
          : [
              `Figma template reference parity metric ${input.name} scored ${input.score}; required ${input.threshold}.`,
            ],
  };
}

function indexCompiledNodesByFigmaNodeId(
  root: FigmaTemplateCompiledNode,
): Map<string, FigmaTemplateCompiledNode> {
  const index = new Map<string, FigmaTemplateCompiledNode>();
  for (const node of flattenCompiledNodes(root)) {
    const figmaNodeId = readCompiledFigmaNodeId(node);
    if (figmaNodeId && !index.has(figmaNodeId)) {
      index.set(figmaNodeId, node);
    }
  }
  return index;
}

function readCompiledFigmaNodeId(
  node: FigmaTemplateCompiledNode,
): string | undefined {
  const fromSource = node.sourceDesign?.figmaNodeId;
  if (typeof fromSource === "string" && fromSource.trim()) {
    return fromSource.trim();
  }
  const fromData = node.dataAttributes?.figmaNodeId;
  if (typeof fromData === "string" && fromData.trim()) {
    return fromData.trim();
  }
  return undefined;
}

function flattenSourceNodes(
  node: FigmaTemplateNode,
  parent?: FigmaTemplateNode,
): SourceNodeEntry[] {
  return [
    { node, parent },
    ...(node.children ?? []).flatMap((child) =>
      flattenSourceNodes(child, node),
    ),
  ];
}

function flattenCompiledNodes(
  node: FigmaTemplateCompiledNode,
): FigmaTemplateCompiledNode[] {
  return [
    node,
    ...(node.children ?? []).flatMap((child) => flattenCompiledNodes(child)),
  ];
}

function hasUsableLayout(
  layout: FigmaTemplateLayoutBox | undefined,
): layout is Required<Pick<FigmaTemplateLayoutBox, "width" | "height">> &
  FigmaTemplateLayoutBox {
  return (
    typeof layout?.width === "number" &&
    layout.width > 0 &&
    typeof layout?.height === "number" &&
    layout.height > 0
  );
}

function parseCssNumber(value: unknown): CssNumber | null {
  if (typeof value === "number" && Number.isFinite(value)) {
    return { value, unit: "number" };
  }
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  const match = /^(-?\d+(?:\.\d+)?)(%|px)?$/.exec(trimmed);
  if (!match) return null;
  return {
    value: Number(match[1]),
    unit: (match[2] as CssNumber["unit"] | undefined) ?? "number",
  };
}

function nearlyEqualCssPx(
  actual: unknown,
  expectedPx: number,
  tolerancePx: number,
): boolean {
  const parsed = parseCssNumber(actual);
  if (!parsed) return false;
  return (
    (parsed.unit === "px" || parsed.unit === "number") &&
    Math.abs(parsed.value - expectedPx) <= tolerancePx
  );
}

function nearlyEqualAspectRatio(
  actual: unknown,
  expectedWidth: number,
  expectedHeight: number,
): boolean {
  if (typeof actual !== "string") return false;
  const match = /^\s*(-?\d+(?:\.\d+)?)\s*\/\s*(-?\d+(?:\.\d+)?)\s*$/.exec(
    actual,
  );
  if (!match) return false;
  const actualRatio = Number(match[1]) / Number(match[2]);
  const expectedRatio = expectedWidth / expectedHeight;
  return Math.abs(actualRatio - expectedRatio) <= 0.0005;
}

function sourceNodeRef(node: FigmaTemplateNode | undefined): string {
  return node
    ? `figma-template:node:${node.id}`
    : "figma-template:node:unknown";
}

function compiledNodeRef(node: FigmaTemplateCompiledNode): string {
  return `compiled:${node.id ?? node.sourceDesign?.figmaNodeId ?? node.dataAttributes?.figmaNodeId ?? "unknown"}`;
}

function roundScore(value: number): number {
  return Number(value.toFixed(3));
}

function unique<T>(items: readonly T[]): T[] {
  return [...new Set(items)];
}
